/**
 * verse-suggest.ts — client caller for the curated-verse-match endpoint
 * (`POST /api/grok-verse-suggest`), the verse lane beside tap-to-match.
 *
 * Contract per rule #59: THROWS on real failure (network, abort,
 * non-OK HTTP, JSON parse, invalid shape) so the consuming surface can
 * render the gentle error + retry state; a 200 with empty verses is a
 * LEGIT honest silence (`silence: 'crisis' | 'none' | 'no-fit'`), not
 * an error.
 *
 * OWNER BINDING (rule #90): this wire boundary rechecks the exact IDB
 * revision, durable owner, auth owner, and auth epoch immediately before fetch. en-US
 * only at v1 (rule #7 carve-out); the consuming surface gates on language
 * like the spine affordances.
 *
 * Returned entries carry refs + segments + the operator context
 * sentence + the jesusWords tier flag; verse TEXT renders from the
 * local BSB (rule #62/#76) — it never crosses this wire.
 */

import {
  authorizePrayerOutbound,
  captureOutboundOwnerBinding,
  outboundOwnerStillCurrent,
  ownerBoundJsonHeaders,
  type PrayerOutboundExpectation,
} from './outbound-prayer-authorization';

const VERSE_SUGGEST_URL = '/api/grok-verse-suggest';
const TIMEOUT_MS = 45_000; // two sequential Grok calls upstream

export type VerseStruggleCategory =
  | 'fear-anxiety'
  | 'trust-faith'
  | 'provision'
  | 'timing-waiting'
  | 'unloved'
  | 'lies-deception'
  | 'loneliness'
  | 'guilt-shame'
  | 'weakness-exhaustion'
  | 'hopelessness'
  | 'gratitude'
  | 'grief-loss';

export interface SuggestedVerse {
  ref: string;
  book: string;
  bookSlug: string;
  chapter: number;
  segments: [number, number][] | null;
  wholeChapter: boolean;
  scale: 'short' | 'chapter';
  jesusWords: boolean;
  wjVerses: number[];
  context: string;
  category: VerseStruggleCategory;
}

export interface VerseSuggestResponse {
  categories: {
    primary: VerseStruggleCategory;
    secondary: VerseStruggleCategory | null;
  } | null;
  /** Jesus-words entries first, then descending fit (server-ordered). */
  verses: SuggestedVerse[];
  silence: 'crisis' | 'none' | 'no-fit' | null;
  crisis?: boolean;
}

export class VerseSuggestError extends Error {
  /** 'rate-limited' gets its own gentle copy; everything else retries. */
  code: 'rate-limited' | 'network' | 'server';
  constructor(code: 'rate-limited' | 'network' | 'server', message: string) {
    super(message);
    this.name = 'VerseSuggestError';
    this.code = code;
  }
}

/**
 * Session-cached variant (the spine-score cache pattern): one paid run
 * per prayer per session, shared by concurrent callers and by the
 * back-from-reader re-render (which would otherwise re-spend the daily
 * bucket). Keyed `prayerId:hash(rawText)` — NOT updatedAt — because
 * persisting the verse result (`verseResult`) bumps updatedAt without
 * changing the prayer text, and an updatedAt key made every
 * reopen-after-save a cache miss (the verse lane visibly "re-searched"
 * a saved match). A real edit changes rawText and still re-fetches.
 * Failures evict so an error never poisons the session; no abort signal
 * on purpose — an unmount lets the call settle into the cache so the
 * next open renders free.
 */
const verseCache = new Map<string, Promise<VerseSuggestResponse>>();

// FNV-1a over the raw text: short stable key without holding prayer
// text in long-lived cache keys.
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

async function authorizeVerseRequest(
  rawText: string,
  expectation: PrayerOutboundExpectation,
): Promise<{ ownerId: string; headers: Record<string, string> }> {
  const binding = captureOutboundOwnerBinding();
  if (
    !binding ||
    expectation.rawText !== rawText
  ) {
    throw new VerseSuggestError('server', 'Prayer authorization is incomplete.');
  }
  const headers = await ownerBoundJsonHeaders(binding);
  if (
    !headers ||
    !(await authorizePrayerOutbound(binding, [expectation])) ||
    !outboundOwnerStillCurrent(binding)
  ) {
    throw new VerseSuggestError('server', 'Prayer changed before scripture search.');
  }
  return { ownerId: binding.ownerId, headers };
}

export async function suggestVersesCached(prayer: {
  id: string;
  updatedAt: string;
  rawText: string;
}, expectation: PrayerOutboundExpectation): Promise<VerseSuggestResponse> {
  const authorized = await authorizeVerseRequest(prayer.rawText, expectation);
  const key = `${authorized.ownerId}:${prayer.id}:${fnv1a(prayer.rawText)}`;
  const hit = verseCache.get(key);
  if (hit) return hit;
  const p = requestSuggestedVerses(prayer.rawText, authorized.headers).catch((err) => {
    verseCache.delete(key);
    throw err;
  });
  verseCache.set(key, p);
  return p;
}

export async function suggestVerses(
  rawText: string,
  expectation: PrayerOutboundExpectation,
  outerSignal?: AbortSignal,
): Promise<VerseSuggestResponse> {
  const authorized = await authorizeVerseRequest(rawText, expectation);
  return requestSuggestedVerses(rawText, authorized.headers, outerSignal);
}

async function requestSuggestedVerses(
  rawText: string,
  headers: Record<string, string>,
  outerSignal?: AbortSignal,
): Promise<VerseSuggestResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const onOuterAbort = () => controller.abort();
  outerSignal?.addEventListener('abort', onOuterAbort, { once: true });

  let res: Response;
  try {
    res = await fetch(VERSE_SUGGEST_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ rawText }),
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof VerseSuggestError) throw err;
    throw new VerseSuggestError('network', `fetch failed: ${(err as Error).name}`);
  } finally {
    clearTimeout(timeout);
    outerSignal?.removeEventListener('abort', onOuterAbort);
  }

  if (res.status === 429) {
    throw new VerseSuggestError('rate-limited', 'daily limit reached');
  }
  if (!res.ok) {
    throw new VerseSuggestError('server', `http ${res.status}`);
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new VerseSuggestError('server', 'invalid JSON');
  }
  const d = data as Partial<VerseSuggestResponse> | null;
  if (!d || !Array.isArray(d.verses)) {
    throw new VerseSuggestError('server', 'invalid shape');
  }
  const silence =
    d.silence === 'crisis' || d.silence === 'none' || d.silence === 'no-fit'
      ? d.silence
      : null;
  return {
    categories: d.categories ?? null,
    verses: d.verses as SuggestedVerse[],
    silence,
    crisis: d.crisis === true,
  };
}
